import { AuthService } from './authService';
import { StcTransactionDto } from '../types/transactionHistory';
import { PagedResult } from '../types/items';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/** Column filter keys that map directly to API query parameters */
const COLUMN_FILTER_API_KEYS = [
  'warehouseId', 'itemNo', 'operatorId', 'binCode',
  'itemDescription', 'orderNoERP', 'orderName', 'reasonDescription'
] as const;

class TransactionHistoryService {
  private getCompanyNo(): string {
    const userData = AuthService.getUserDataFromToken();
    if (!userData?.companyNo) {
      throw new Error('No company data available');
    }
    return userData.companyNo;
  }

  private buildFilterParams(
    fromDate: Date,
    toDate: Date,
    warehouseId?: string,
    itemNo?: string,
    transType?: number,
    operatorId?: string
  ): URLSearchParams {
    const params = new URLSearchParams();
    params.append('fromDate', fromDate.toISOString());
    params.append('toDate', toDate.toISOString());

    if (warehouseId && warehouseId.trim() !== '') {
      params.append('warehouseId', warehouseId);
    }
    if (itemNo && itemNo.trim() !== '') {
      params.append('itemNo', itemNo);
    }
    if (transType !== null && transType !== undefined) {
      params.append('transType', transType.toString());
    }
    if (operatorId && operatorId.trim() !== '') {
      params.append('operatorId', operatorId);
    }
    return params;
  }

  /**
   * Get transaction history with optional filters (returns all rows - legacy)
   */
  async getTransactionHistory(
    fromDate: Date,
    toDate: Date,
    warehouseId?: string,
    itemNo?: string,
    transType?: number,
    operatorId?: string
  ): Promise<StcTransactionDto[]> {
    const companyNo = this.getCompanyNo();
    const params = this.buildFilterParams(fromDate, toDate, warehouseId, itemNo, transType, operatorId);

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/stctransaction/history?${params.toString()}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get transaction history: ${response.status} ${response.statusText}. ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Get transaction history with server-side pagination and column filters
   */
  async getTransactionHistoryPaged(
    fromDate: Date,
    toDate: Date,
    pageNumber: number,
    pageSize: number,
    columnFilters?: Record<string, string>,
    dropdownFilters?: Record<string, string[]>,
    sortColumn?: string,
    sortDirection?: string
  ): Promise<PagedResult<StcTransactionDto>> {
    const companyNo = this.getCompanyNo();
    const params = new URLSearchParams();
    params.append('fromDate', fromDate.toISOString());
    params.append('toDate', toDate.toISOString());
    params.append('pageNumber', pageNumber.toString());
    params.append('pageSize', pageSize.toString());

    if (sortColumn) {
      params.append('sortColumn', sortColumn);
    }
    if (sortDirection) {
      params.append('sortDirection', sortDirection);
    }

    // Map column text filter keys to API parameter names
    if (columnFilters) {
      const keyMap: Record<string, string> = {
        'warehouseID': 'warehouseId',
        'itemNo': 'itemNo',
        'operatorID': 'operatorId',
        'binCode': 'binCode',
        'itemDescription': 'itemDescription',
        'orderNoERP': 'orderNoERP',
        'orderName': 'orderName',
        'reasonDescription': 'reasonDescription',
        'serialNo': 'serialNo',
        'batchNo': 'batchNo',
        'expiryDate': 'expiryDate',
        // B2-fiks: disse manglet, så filteret gjorde ingenting (UI uten effekt)
        'pListID': 'pListID',
        'pListLineNo': 'pListLineNo',
        'projectNo': 'projectNo',
        'depNo': 'depNo',
        'lineNoERP': 'lineNoERP',
        'grShipmentNo': 'grShipmentNo',
      };

      for (const [columnKey, apiParam] of Object.entries(keyMap)) {
        const value = columnFilters[columnKey];
        if (value && value.trim() !== '') {
          params.append(apiParam, value.trim());
        }
      }

      // transType text input: try to parse as number for exact match
      const transTypeFilter = columnFilters['transTypeDescription'];
      if (transTypeFilter && transTypeFilter.trim() !== '') {
        const parsed = parseInt(transTypeFilter, 10);
        if (!isNaN(parsed)) {
          params.append('transType', parsed.toString());
        }
      }
    }

    // Map dropdown filter selections to API parameters
    // Dropdown values are exact matches — for columns with filterType 'both',
    // the user can pick from a dropdown OR type in the text field.
    // Dropdown selections override text filters for the same column.
    if (dropdownFilters) {
      const dropdownKeyMap: Record<string, string> = {
        'warehouseID': 'warehouseId',
        'itemNo': 'itemNo',
        'operatorID': 'operatorId',
        'binCode': 'binCode',
        'itemDescription': 'itemDescription',
        'orderNoERP': 'orderNoERP',
        'orderName': 'orderName',
        'reasonDescription': 'reasonDescription',
        'serialNo': 'serialNo',
        'batchNo': 'batchNo',
        'expiryDate': 'expiryDate',
      };

      for (const [columnKey, apiParam] of Object.entries(dropdownKeyMap)) {
        const values = dropdownFilters[columnKey];
        if (values && values.length > 0) {
          // Send all selected values comma-joined for multi-select support
          // Backend uses STRING_SPLIT + IN when value contains comma, LIKE otherwise
          params.delete(apiParam);
          params.append(apiParam, values.join(','));
        }
      }

      // transTypeDescription dropdown: send selected description texts for server-side IN matching
      const transTypeDescriptions = dropdownFilters['transTypeDescription'];
      if (transTypeDescriptions && transTypeDescriptions.length > 0) {
        params.delete('transTypeDescription');
        params.append('transTypeDescription', transTypeDescriptions.join(','));
      }
    }

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/stctransaction/history/paged?${params.toString()}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get transaction history: ${response.status} ${response.statusText}. ${errorText}`);
    }

    return await response.json();
  }
  /**
   * Get distinct filter values for dropdown options within a date range
   */
  async getFilterOptions(
    fromDate: Date,
    toDate: Date
  ): Promise<TransactionFilterOptions> {
    const companyNo = this.getCompanyNo();
    const params = new URLSearchParams();
    params.append('fromDate', fromDate.toISOString());
    params.append('toDate', toDate.toISOString());

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/stctransaction/history/filter-options?${params.toString()}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get filter options: ${response.status} ${response.statusText}. ${errorText}`);
    }

    return await response.json();
  }
}

export interface TransactionFilterOptions {
  warehouseIds: string[];
  itemNos: string[];
  operatorIds: string[];
  binCodes: string[];
  // transTypes fjernet 2026-07-07: Type-filteret er kode-basert via useStatusOptions(27)
  itemDescriptions: string[];
  orderNoERPs: string[];
  orderNames: string[];
  reasonDescriptions: string[];
}

export const transactionHistoryService = new TransactionHistoryService();
